import { authHeaders, throwOnError } from "./authHeaders";

/** Client for /api/reports/recipients and /api/reports/send (automated compliance reports). */

export type ReportType = "daily_facility" | "daily_combined" | "weekly_enterprise";

export interface ReportRecipient {
  id: string;
  report_type: ReportType;
  hospital_id: string | null;
  hospital_name: string | null;
  email: string;
  name: string | null;
  active: boolean;
}

export interface ReportRecipientsResponse {
  email_configured: boolean;
  /** May add/remove recipients (named recipient managers only). */
  can_manage: boolean;
  can_manage_enterprise: boolean;
  /** May see the enterprise-wide reports (all-hospital access). */
  can_view_enterprise: boolean;
  schedule: { daily: string; weekly: string };
  facilities: { id: string; name: string; checkpoints: number }[];
  recipients: ReportRecipient[];
  last_sent: Partial<Record<ReportType, { at: string; total: number }>>;
}

export async function fetchReportRecipients(): Promise<ReportRecipientsResponse> {
  const r = await fetch("/api/reports/recipients", { headers: authHeaders() });
  await throwOnError(r, "Failed to load report recipients");
  return r.json();
}

export async function addReportRecipient(body: { report_type: ReportType; hospital_id?: string | null; email: string; name?: string }) {
  const r = await fetch("/api/reports/recipients", { method: "POST", headers: authHeaders(true), body: JSON.stringify(body) });
  await throwOnError(r, "Failed to add recipient");
  return r.json();
}

export async function removeReportRecipient(id: string) {
  const r = await fetch(`/api/reports/recipients?id=${encodeURIComponent(id)}`, { method: "DELETE", headers: authHeaders() });
  await throwOnError(r, "Failed to remove recipient");
  return r.json();
}

function sendUrl(mode: "preview" | "test", type: ReportType, hospitalId?: string | null) {
  const p = new URLSearchParams({ mode, type });
  if (hospitalId) p.set("hospital_id", hospitalId);
  return `/api/reports/send?${p.toString()}`;
}

export async function previewReport(type: ReportType, hospitalId?: string | null): Promise<{ subject: string; html: string }> {
  const r = await fetch(sendUrl("preview", type, hospitalId), { headers: authHeaders() });
  await throwOnError(r, "Failed to build preview");
  return r.json();
}

export async function sendTestReport(type: ReportType, hospitalId?: string | null): Promise<{ sent: boolean; to: string }> {
  const r = await fetch(sendUrl("test", type, hospitalId), { method: "POST", headers: authHeaders() });
  await throwOnError(r, "Failed to send test email");
  return r.json();
}
